/**
 * Shared Face Identity + Salon franchise device login session.
 * Session JWT is HttpOnly cookie only — never stored in sessionStorage (APK/WebView safe).
 */

import { getSalonAdminApiBaseUrl } from '../salon-admin/utils/apiConfig';
import { fetchWithTimeout } from '../salon/utils/fetchWithTimeout';
import { clearAllGuestKioskState } from '../face-identity/utils/faceIdentityApi';
import { clearDeviceLocationCache, getDeviceLocationHeaders } from './deviceLocationCache';
import { clearSalonGuestPii, clearSalonLastCustomerSnapshot } from '../salon/utils/salonGuestPiiStore';
import { clearSalonFaceSession } from '../salon/utils/salonFaceSession';
import { clearSalonAppSessionToken } from '../salon/utils/salonAppSessionToken';
import { clearKioskPoweredByFlag, syncKioskPoweredByFlag } from './kioskPoweredBy';
import { clearFranchiseBrandStorage, persistFranchiseBrand } from './franchiseBrand';
import { persistSalonHubOrgServices } from '../../beauty-shared/config/salonHubEntitlements';
export const FRANCHISE_FLOW_STORAGE_KEY = 'naturals_franchise_flow_auth';
/** Survives APK/WebView process death so reopen resumes without re-login (cleared only on logout). */
export const FRANCHISE_FLOW_LAST_PATH_KEY = 'naturals_franchise_flow_last_path';
/** Keep in sync with guestFlowAuth.GUEST_FLOW_STORAGE_KEY (avoid circular import). */
const GUEST_FLOW_STORAGE_KEY = 'naturals_guest_flow_auth';
/** Auto sign-out window for kiosk device sessions (aligned with JWT expiry). */
export const FRANCHISE_FLOW_SESSION_MAX_MS = 12 * 60 * 60 * 1000;

let kioskBootstrapToken = '';

export function getKioskBootstrapToken() {
  return kioskBootstrapToken;
}

function setKioskBootstrapToken(value) {
  kioskBootstrapToken = String(value || '').trim();
}

function adminApiBase() {
  return (getSalonAdminApiBaseUrl() || '').replace(/\/$/, '');
}

function isSessionExpired(auth) {
  if (!auth) return true;
  const started = Number(auth.signed_in_at || auth.saved_at || 0);
  if (!Number.isFinite(started) || started <= 0) return true;
  return Date.now() - started >= FRANCHISE_FLOW_SESSION_MAX_MS;
}

/** Milliseconds remaining before auto sign-out; 0 if expired/missing. */
export function getFranchiseFlowSessionRemainingMs(auth = readFranchiseFlowAuthRaw()) {
  if (!auth) return 0;
  const started = Number(auth.signed_in_at || auth.saved_at || 0);
  if (!Number.isFinite(started) || started <= 0) return 0;
  return Math.max(0, FRANCHISE_FLOW_SESSION_MAX_MS - (Date.now() - started));
}

function sanitizeStoredAuth(parsed) {
  if (!parsed || typeof parsed !== 'object') return null;
  if (!parsed.organization_id) return null;
  const next = { ...parsed };
  delete next.access_token;
  delete next.token;
  delete next.kiosk_bootstrap_token;
  return next;
}

function attachBootstrapToken(auth) {
  if (!auth) return null;
  const token = kioskBootstrapToken || null;
  return token ? { ...auth, kiosk_bootstrap_token: token } : auth;
}

function readFranchiseFlowAuthRaw() {
  try {
    let raw = sessionStorage.getItem(FRANCHISE_FLOW_STORAGE_KEY);
    if (!raw) {
      try {
        raw = localStorage.getItem(FRANCHISE_FLOW_STORAGE_KEY);
        if (raw) {
          sessionStorage.setItem(FRANCHISE_FLOW_STORAGE_KEY, raw);
        }
      } catch {
        /* ignore */
      }
    }
    if (!raw) return null;
    return sanitizeStoredAuth(JSON.parse(raw));
  } catch {
    return null;
  }
}

function writeFranchiseFlowAuthRaw(next) {
  const json = JSON.stringify(next);
  try {
    sessionStorage.setItem(FRANCHISE_FLOW_STORAGE_KEY, json);
  } catch {
    /* ignore */
  }
  try {
    localStorage.setItem(FRANCHISE_FLOW_STORAGE_KEY, json);
  } catch {
    /* ignore */
  }
}

export function readFranchiseFlowAuth() {
  const auth = readFranchiseFlowAuthRaw();
  if (!auth) return null;
  if (isSessionExpired(auth)) {
    clearFranchiseFlowAuth();
    return null;
  }
  return attachBootstrapToken(auth);
}

/** Kiosk API headers — cookie session + device GPS; no bearer token in storage. */
export function franchiseAuthHeaders(extra = {}) {
  return getDeviceLocationHeaders(extra);
}

export function persistFranchiseFlowAuth(payload, { renewSignedInAt = false } = {}) {
  const existing = readFranchiseFlowAuthRaw();
  const previousSignedInAt = Number(existing?.signed_in_at || existing?.saved_at || 0);
  const signedInAt =
    renewSignedInAt || !Number.isFinite(previousSignedInAt) || previousSignedInAt <= 0
      ? Date.now()
      : previousSignedInAt;

  const next = {
    organization_id: String(payload.organization_id),
    franchise_id: payload.franchise_id ?? null,
    login_account_id: payload.login_account_id ?? payload.stylist_id ?? null,
    stylist_id: payload.stylist_id ?? payload.login_account_id ?? null,
    username: payload.username || '',
    full_name: payload.full_name || payload.name || payload.username || '',
    franchise_name: payload.franchise_name || '',
    franchise_code: payload.franchise_code || null,
    franchise_logo: payload.franchise_logo || payload.org_logo || '',
    org_logo: payload.franchise_logo || payload.org_logo || '',
    org_name: payload.franchise_name || payload.organization_name || payload.org_name || '',
    latitude:
      payload.latitude != null && payload.latitude !== ''
        ? Number(payload.latitude)
        : null,
    longitude:
      payload.longitude != null && payload.longitude !== ''
        ? Number(payload.longitude)
        : null,
    has_store_location: Boolean(
      payload.has_store_location ||
        (payload.latitude != null &&
          payload.longitude != null &&
          Number.isFinite(Number(payload.latitude)) &&
          Number.isFinite(Number(payload.longitude)))
    ),
    geofence_radius_m: Number(payload.geofence_radius_m) || 250,
    geofence_enabled: Boolean(
      payload.geofence_enabled !== undefined && payload.geofence_enabled !== null
        ? payload.geofence_enabled
        : existing?.geofence_enabled,
    ),
    must_reset_password: Boolean(payload.must_reset_password),
    powered_by_nyafaz:
      payload.powered_by_nyafaz !== undefined
        ? payload.powered_by_nyafaz !== false
        : existing?.powered_by_nyafaz !== false,
    passport_checkin_enabled:
      payload.passport_checkin_enabled !== undefined
        ? payload.passport_checkin_enabled === true
        : existing?.passport_checkin_enabled === true,
    enabled_services: Array.isArray(payload.enabled_services)
      ? payload.enabled_services
      : Array.isArray(payload.enabledServices)
        ? payload.enabledServices
        : existing?.enabled_services || [],
    signed_in_at: signedInAt,
    saved_at: Date.now(),
  };
  if (payload.powered_by_nyafaz !== undefined || existing?.powered_by_nyafaz !== undefined) {
    syncKioskPoweredByFlag(next.powered_by_nyafaz);
  }
  persistFranchiseBrand({
    logo: next.franchise_logo || next.org_logo,
    name: next.franchise_name || next.org_name,
  });
  if (payload.kiosk_bootstrap_token !== undefined) {
    const incoming = String(payload.kiosk_bootstrap_token || '').trim();
    if (incoming) setKioskBootstrapToken(incoming);
  }
  if (!Number.isFinite(next.latitude)) next.latitude = null;
  if (!Number.isFinite(next.longitude)) next.longitude = null;
  writeFranchiseFlowAuthRaw(next);
  if (next.enabled_services?.length) {
    persistSalonHubOrgServices(next.enabled_services);
  }
  return attachBootstrapToken(next);
}

const GUEST_KIOSK_STORAGE_KEYS = [
  'enterprise_nxt_face_profile',
  'enterprise_nxt_face_pending',
  'enterprise_nxt_face_capture',
  'enterprise_salon_face_checked_in',
  'enterprise_salon_face_user_id',
  'enterprise_salon_face_mobile',
  'enterprise_salon_face_name',
  'enterprise_salon_face_email',
  'enterprise_salon_face_gender',
  'enterprise_salon_face_org',
  'enterprise_salon_face_dob',
  'enterprise_salon_face_age',
  'enterprise_salon_compare_previous',
  'enterprise_salon_visit_reference',
  'enterprise_salon_entry_source',
  'enterprise_salon_active_checkin_id',
  'enterprise_salon_active_checkin_code',
  'enterprise_salon_active_checkin_status',
  'enterprise_salon_visit_handoff',
  'enterprise_salon_selected_services',
  'enterprise_salon_selected_service_names',
  'enterprise_salon_pending_checkin_id',
  'enterprise_salon_pending_checkin_code',
];

export function clearFranchiseFlowAuth({ keepLastPath = false } = {}) {
  setKioskBootstrapToken('');
  clearKioskPoweredByFlag();
  clearFranchiseBrandStorage();
  clearSalonAppSessionToken();
  try {
    sessionStorage.removeItem(FRANCHISE_FLOW_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(FRANCHISE_FLOW_STORAGE_KEY);
    if (!keepLastPath) {
      localStorage.removeItem(FRANCHISE_FLOW_LAST_PATH_KEY);
    }
  } catch {
    /* ignore */
  }
  clearDeviceLocationCache();
  try {
    clearSalonGuestPii();
    clearSalonLastCustomerSnapshot();
    clearSalonFaceSession();
  } catch {
    /* ignore */
  }
  try {
    GUEST_KIOSK_STORAGE_KEYS.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem('orgId');
    localStorage.removeItem('organization_id');
  } catch {
    /* ignore */
  }
}

export function persistFranchiseFlowLastPath(path) {
  const next = String(path || '').trim();
  if (!next || next.includes('/login') || isGuestKioskPathSafe(next)) return;
  try {
    localStorage.setItem(FRANCHISE_FLOW_LAST_PATH_KEY, next);
  } catch {
    /* ignore */
  }
}

export function readFranchiseFlowLastPath() {
  try {
    return String(localStorage.getItem(FRANCHISE_FLOW_LAST_PATH_KEY) || '').trim();
  } catch {
    return '';
  }
}

export function clearFranchiseFlowLastPath() {
  try {
    localStorage.removeItem(FRANCHISE_FLOW_LAST_PATH_KEY);
  } catch {
    /* ignore */
  }
}

function isGuestKioskPathSafe(path) {
  return /\/naturals\/(?:face-identity|passport|salon)(?:\/guest)?\/(?:welcome|register|pending-registration|mobile)(?:\/|$|\?)/i.test(
    String(path || '').split('#')[0],
  );
}

/** Strip bootstrap token from legacy sessionStorage auth blob. */
export function purgeLegacyFranchiseFlowBootstrapTokenStorage() {
  try {
    const raw =
      sessionStorage.getItem(FRANCHISE_FLOW_STORAGE_KEY) ||
      localStorage.getItem(FRANCHISE_FLOW_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return;
    if (!parsed.kiosk_bootstrap_token) return;
    delete parsed.kiosk_bootstrap_token;
    writeFranchiseFlowAuthRaw(parsed);
  } catch {
    /* ignore */
  }
}

export function hasFranchiseFlowAuth() {
  return Boolean(readFranchiseFlowAuth()?.organization_id);
}

export function getFranchiseOrganizationId() {
  const auth = readFranchiseFlowAuth();
  return auth?.organization_id ? String(auth.organization_id) : '';
}

export function getFranchiseId() {
  const auth = readFranchiseFlowAuth();
  const id = auth?.franchise_id;
  return id != null && String(id).trim() !== '' ? String(id) : '';
}

/** Append org + franchise scope for makeup/hair kiosk APIs (JWT cookie + query for tenant jobs). */
export function appendFranchiseScopeQueryToPath(path) {
  let out = String(path || '');
  const orgId = getFranchiseOrganizationId();
  const franchiseId = getFranchiseId();
  const add = (key, val) => {
    if (!val) return;
    const join = out.includes('?') ? '&' : '?';
    out = `${out}${join}${key}=${encodeURIComponent(String(val))}`;
  };
  add('organization_id', orgId);
  add('franchise_id', franchiseId);
  return out;
}

/** HQ franchise flag: Passport face kiosk vs direct mobile / stylist check-in only. */
export function isFranchisePassportCheckinEnabled(auth = readFranchiseFlowAuth()) {
  return auth?.passport_checkin_enabled === true;
}

/** organization_id + franchise_id for salon kiosk APIs (franchise + guest fallbacks). */
export function resolveFranchiseScopeIds() {
  let orgId = getFranchiseOrganizationId();
  let franchiseId = getFranchiseId();
  if (!orgId || !franchiseId) {
    try {
      const raw = sessionStorage.getItem(GUEST_FLOW_STORAGE_KEY);
      const guest = raw ? JSON.parse(raw) : null;
      if (!orgId && guest?.organization_id) {
        orgId = String(guest.organization_id);
      }
      if (!franchiseId && guest?.franchise_id != null && String(guest.franchise_id).trim() !== '') {
        franchiseId = String(guest.franchise_id);
      }
    } catch {
      /* ignore */
    }
  }
  if (!franchiseId && typeof window !== 'undefined') {
    try {
      franchiseId = String(
        new URLSearchParams(window.location.search || '').get('franchise_id') || '',
      ).trim();
    } catch {
      franchiseId = '';
    }
  }
  return {
    organizationId: orgId || '',
    franchiseId: franchiseId || '',
  };
}

/**
 * Append organization_id + franchise_id so page-setup / kiosk-landing APIs
 * resolve the franchise-assigned Theme & page setup pack (not HQ live defaults).
 * @param {URLSearchParams|Record<string,string>|string|null} [params]
 * @returns {URLSearchParams}
 */
export function appendFranchiseThemeParams(params) {
  const qs =
    params instanceof URLSearchParams
      ? params
      : new URLSearchParams(params || undefined);
  let orgId = getFranchiseOrganizationId();
  let franchiseId = getFranchiseId();
  if (!orgId || !franchiseId) {
    try {
      const raw = sessionStorage.getItem(GUEST_FLOW_STORAGE_KEY);
      const guest = raw ? JSON.parse(raw) : null;
      if (!orgId && guest?.organization_id) {
        orgId = String(guest.organization_id);
      }
      if (!franchiseId && guest?.franchise_id != null && String(guest.franchise_id).trim() !== '') {
        franchiseId = String(guest.franchise_id);
      }
    } catch {
      /* ignore */
    }
  }
  if (orgId) qs.set('organization_id', orgId);
  if (!franchiseId && typeof window !== 'undefined') {
    try {
      franchiseId = String(
        new URLSearchParams(window.location.search || '').get('franchise_id') || '',
      ).trim();
    } catch {
      franchiseId = '';
    }
  }
  if (franchiseId) qs.set('franchise_id', franchiseId);
  return qs;
}

export function getFranchiseCode() {
  const auth = readFranchiseFlowAuth();
  const code = auth?.franchise_code;
  return code != null && String(code).trim() !== '' ? String(code) : '';
}

export function getFranchiseStylistId() {
  const auth = readFranchiseFlowAuth();
  const id = auth?.stylist_id ?? auth?.login_account_id;
  return id != null && String(id).trim() !== '' ? id : null;
}

export function mustResetFranchisePassword() {
  return Boolean(readFranchiseFlowAuth()?.must_reset_password);
}

export async function loginFranchiseFlow({ username, password }) {
  const base = adminApiBase();
  if (!base) throw new Error('Salon admin API URL is not configured');
  const res = await fetchWithTimeout(`${base}/franchise-flow/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ username, password }),
    timeoutMs: 20000,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || data.message || `Login failed (${res.status})`);
  }
  return persistFranchiseFlowAuth(data, { renewSignedInAt: true });
}

export async function validateFranchiseFlowAuth() {
  if (!hasFranchiseFlowAuth()) return null;
  const base = adminApiBase();
  if (!base) return readFranchiseFlowAuth();
  try {
    const res = await fetchWithTimeout(`${base}/franchise-flow/me`, {
      method: 'GET',
      credentials: 'include',
      timeoutMs: 12000,
    });
    if (!res.ok) {
      clearFranchiseFlowAuth({ keepLastPath: true });
      return null;
    }
    const data = await res.json();
    const existing = readFranchiseFlowAuth() || {};
    return persistFranchiseFlowAuth(
      {
        ...existing,
        ...data,
        signed_in_at: existing.signed_in_at || existing.saved_at,
      },
      { renewSignedInAt: false },
    );
  } catch {
    return readFranchiseFlowAuth();
  }
}

export async function updateFranchiseFlowPassword({ currentPassword, newPassword }) {
  const base = adminApiBase();
  if (!base) throw new Error('Salon admin API URL is not configured');
  const res = await fetchWithTimeout(`${base}/franchise-flow/update-my-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
    timeoutMs: 20000,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || data.message || 'Password update failed');
  }
  const auth = readFranchiseFlowAuth();
  if (auth) {
    persistFranchiseFlowAuth({ ...auth, must_reset_password: false }, { renewSignedInAt: false });
  }
  return data;
}

export async function revokeFranchiseFlowServerSession() {
  const base = adminApiBase();
  if (!base || !hasFranchiseFlowAuth()) return;
  try {
    await fetchWithTimeout(`${base}/franchise-flow/logout`, {
      method: 'POST',
      credentials: 'include',
      timeoutMs: 8000,
    });
  } catch {
    /* ignore */
  }
}

export async function logoutFranchiseFlow() {
  const base = adminApiBase();
  if (base && hasFranchiseFlowAuth()) {
    try {
      await fetchWithTimeout(`${base}/franchise-flow/logout`, {
        method: 'POST',
        credentials: 'include',
        timeoutMs: 8000,
      });
    } catch {
      /* ignore */
    }
  }
  clearFranchiseFlowAuth();
  try {
    clearAllGuestKioskState();
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem('enterprise_salon_detection_session_id');
  } catch {
    /* ignore */
  }
}
